import { getIntl } from 'umi';

import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';

import {
  API_ROUTE,
  MODEL_NAME,
  PAYMENT_REVIEW_ACTION,
} from './constants';
import {
  areReportedFactsLocked,
  applyAttentionListFilters,
  buildClaimSubmitPayload,
  buildConfirmPayload,
  clearClaimIdempotencyKey,
  filtersConfig,
  getOrCreateClaimIdempotencyKey,
  isPendingManualClaim,
  unwrapPaymentList,
} from './utils';

const api = bindServices(MODEL_NAME);

const getHttpStatus = (error) => error?.status || error?.response?.status || error?.data?.status;

const getUnknownErrorMessage = () => getIntl().formatMessage({ id: 'global.error.unknown' });

const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: null,
  select: null,
  searchSelect: null,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  activatingItemIds: {},
  filtersConfig,
  claimModal: { state: false, subscriptionId: null, subscription: null },
  reviewModal: { state: false, id: null, action: null },
  subscriptionPayments: {},
  subscriptionPaymentsLoading: {},
  submittingClaim: false,
  reviewing: false,
};

const BillingSubscriptionPaymentsModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const res = yield call(api.getAll, {
          route: `${API_ROUTE}/attention`,
          params: {
            ...(payload?.filter && { filter: payload.filter }),
            ...(payload?.search && { search: payload.search }),
            ...(payload?.orderBy && { orderBy: payload.orderBy }),
          },
          readPriority: true,
        });
        const rawItems = unwrapPaymentList(res);
        const mapped = rawItems.map((item) => ({
          ...item,
          // ConfigListPage rowKey / detail id: prefer pending payment when present.
          id: item.lastPaymentId || item.companyBillingSubscriptionId,
          paymentId: item.lastPaymentId || null,
          attentionSubscriptionId: item.companyBillingSubscriptionId,
        }));
        const moduleFilters = yield select(
          (state) => state.global?.globalFilters?.[MODEL_NAME],
        );
        const items = applyAttentionListFilters(mapped, moduleFilters, payload?.search);
        yield put({
          type: 'queryData',
          payload: {
            items,
            limit: items.length || 20,
            page: 1,
            totalCount: items.length,
          },
        });
      } catch (e) {
        /* global effect handler */
      }
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        if (!payload?.id) return;
        yield put({ type: 'success', payload: { success: false } });

        const list = yield select((state) => state[MODEL_NAME].list) || [];
        const fromList = list.find((row) => row.id === payload.id || row.paymentId === payload.id);
        const paymentId = payload.paymentId || fromList?.paymentId || payload.id;

        try {
          const res = yield call(api.getById, {
            route: API_ROUTE,
            id: paymentId,
          });
          const item = Array.isArray(res) ? res[0] : res;
          if (item?.id) {
            yield put({
              type: 'queryItem',
              payload: {
                ...item,
                attentionSubscriptionId:
                  item.companyBillingSubscriptionId || fromList?.attentionSubscriptionId,
                companyName: fromList?.companyName,
                planName: fromList?.planName,
                netAmountDue: fromList?.netAmountDue,
              },
            });
            return;
          }
        } catch (e) {
          /* fall through to attention snapshot */
        }

        if (fromList) {
          yield put({ type: 'queryItem', payload: fromList });
        }
      } catch (e) {
        /* global effect handler */
      }
    },

    *fetchSubscriptionPayments({ payload }, { call, put }) {
      const subscriptionId = payload?.subscriptionId;
      if (!subscriptionId) return;

      yield put({
        type: 'setSubscriptionPaymentsLoading',
        payload: { subscriptionId, loading: true },
      });

      try {
        const res = yield call(api.getAll, {
          route: `${API_ROUTE}/subscription/${subscriptionId}`,
          readPriority: true,
        });
        const items = unwrapPaymentList(res);
        yield put({
          type: 'setSubscriptionPayments',
          payload: { subscriptionId, items },
        });
      } catch (e) {
        yield put({
          type: 'setSubscriptionPayments',
          payload: { subscriptionId, items: [] },
        });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({
          type: 'setSubscriptionPaymentsLoading',
          payload: { subscriptionId, loading: false },
        });
      }
    },

    *showClaimModal({ payload }, { put }) {
      yield put({ type: 'queryShowClaimModal', payload });
      if (payload?.state && payload?.subscriptionId) {
        yield put({
          type: 'fetchSubscriptionPayments',
          payload: { subscriptionId: payload.subscriptionId },
        });
      }
    },

    *closeClaimModal(_, { put }) {
      yield put({
        type: 'queryShowClaimModal',
        payload: { state: false, subscriptionId: null, subscription: null },
      });
    },

    *showReviewModal({ payload }, { put }) {
      yield put({ type: 'queryShowReviewModal', payload });
    },

    *closeReviewModal(_, { put }) {
      yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
    },

    *submitManualClaim({ payload }, { call, put, select }) {
      const {
        companyBillingSubscriptionId,
        provider,
        grossAmount,
        reportedPaymentDate,
        externalReference,
        evidenceDocumentId,
        notes,
      } = payload || {};

      if (!companyBillingSubscriptionId) return;

      const idempotencyKey =
        payload?.idempotencyKey || getOrCreateClaimIdempotencyKey(companyBillingSubscriptionId);

      const data = buildClaimSubmitPayload({
        companyBillingSubscriptionId,
        provider,
        grossAmount,
        reportedPaymentDate,
        externalReference,
        evidenceDocumentId,
        notes,
        idempotencyKey,
      });

      yield put({ type: 'setSubmittingClaim', payload: true });
      yield put({ type: 'success', payload: { success: false } });

      try {
        const res = yield call(api.apiRoute, {
          route: `${API_ROUTE}/manual`,
          method: 'post',
          data,
        });
        const payment = Array.isArray(res) ? res[0] : res;

        yield put({
          type: 'upsertSubscriptionPayment',
          payload: { subscriptionId: companyBillingSubscriptionId, payment },
        });
        yield put({ type: 'queryShowClaimModal', payload: { state: false, subscriptionId: null } });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        if (payment?.id) {
          yield put({ type: 'queryItem', payload: payment });
        }
      } catch (e) {
        const status = getHttpStatus(e);
        if (status === 400 && e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
          const currentId = yield select((state) => state[MODEL_NAME].id);
          if (currentId) {
            yield put({ type: 'fetchItem', payload: { id: currentId } });
          } else {
            yield put({
              type: 'fetchSubscriptionPayments',
              payload: { subscriptionId: companyBillingSubscriptionId },
            });
          }
          return;
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        } else {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: getUnknownErrorMessage(),
            },
          });
        }
      } finally {
        yield put({ type: 'setSubmittingClaim', payload: false });
      }
    },

    *reviewPayment({ payload }, { call, put, select }) {
      const { id, action, reason, confirm } = payload || {};
      const isConfirm = action === PAYMENT_REVIEW_ACTION.CONFIRM;
      const isReject = action === PAYMENT_REVIEW_ACTION.REJECT;
      if (!id || (!isConfirm && !isReject)) return;

      if (isReject && !String(reason || '').trim()) return;

      yield put({ type: 'setReviewing', payload: true });
      yield put({ type: 'success', payload: { success: false } });

      try {
        const freshRes = yield call(api.getById, { route: API_ROUTE, id });
        const fresh = Array.isArray(freshRes) ? freshRes[0] : freshRes;
        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (currentId === id) {
          yield put({ type: 'queryItem', payload: fresh });
        }

        if (!isPendingManualClaim(fresh)) {
          yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
          yield put({ type: 'success', payload: { success: true } });
          return;
        }

        let reviewRes;
        if (isReject) {
          reviewRes = yield call(api.apiRoute, {
            route: `${API_ROUTE}/manual/${id}/reject`,
            method: 'post',
            data: { reason: String(reason).trim() },
          });
          clearClaimIdempotencyKey(fresh.companyBillingSubscriptionId);
        } else {
          reviewRes = yield call(api.apiRoute, {
            route: `${API_ROUTE}/manual/${id}/confirm`,
            method: 'post',
            data: buildConfirmPayload(confirm || {}),
          });
          clearClaimIdempotencyKey(fresh.companyBillingSubscriptionId);
        }

        const reviewed = Array.isArray(reviewRes) ? reviewRes[0] : reviewRes;
        yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        if (currentId === id && reviewed?.id) {
          yield put({ type: 'queryItem', payload: reviewed });
        } else if (currentId === id) {
          yield put({ type: 'fetchItem', payload: { id } });
        }

        if (reviewed?.companyBillingSubscriptionId) {
          yield put({
            type: 'upsertSubscriptionPayment',
            payload: {
              subscriptionId: reviewed.companyBillingSubscriptionId,
              payment: reviewed,
            },
          });
        }
      } catch (e) {
        const status = getHttpStatus(e);
        if (status === 400 && e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
          yield put({ type: 'fetchItem', payload: { id } });
          yield put({ type: 'fetchList', payload: {} });
          return;
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setReviewing', payload: false });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    queryShowClaimModal(state, { payload }) {
      return {
        ...state,
        claimModal: {
          state: !!payload?.state,
          subscriptionId: payload?.subscriptionId ?? null,
          subscription: payload?.subscription ?? null,
        },
      };
    },
    queryShowReviewModal(state, { payload }) {
      return {
        ...state,
        reviewModal: {
          state: !!payload?.state,
          id: payload?.id ?? null,
          action: payload?.action ?? null,
        },
      };
    },
    setSubmittingClaim(state, { payload }) {
      return { ...state, submittingClaim: !!payload };
    },
    setReviewing(state, { payload }) {
      return { ...state, reviewing: !!payload };
    },
    setSubscriptionPaymentsLoading(state, { payload }) {
      const { subscriptionId, loading } = payload || {};
      if (!subscriptionId) return state;
      return {
        ...state,
        subscriptionPaymentsLoading: {
          ...state.subscriptionPaymentsLoading,
          [subscriptionId]: !!loading,
        },
      };
    },
    setSubscriptionPayments(state, { payload }) {
      const { subscriptionId, items } = payload || {};
      if (!subscriptionId) return state;
      return {
        ...state,
        subscriptionPayments: {
          ...state.subscriptionPayments,
          [subscriptionId]: Array.isArray(items) ? items : [],
        },
      };
    },
    upsertSubscriptionPayment(state, { payload }) {
      const { subscriptionId, payment } = payload || {};
      if (!subscriptionId || !payment?.id) return state;
      const existing = state.subscriptionPayments[subscriptionId] || [];
      const next = [payment, ...existing.filter((item) => item.id !== payment.id)];
      return {
        ...state,
        subscriptionPayments: {
          ...state.subscriptionPayments,
          [subscriptionId]: next,
        },
        ...(areReportedFactsLocked(payment) && state.id === payment.id
          ? { item: payment }
          : {}),
      };
    },
  },
};

export default BillingSubscriptionPaymentsModel;
